
import '../styles.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'dayjs/locale/de'
import 'dayjs/locale/nl'
import { StrictMode, Fragment } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from '../api/store';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { BrowserRouter as RouterProvider, Routes, Route } from "react-router-dom";
import { ApolloProviderWrapper as SubscriptionProvider } from '../api/graphqlSubscription';
import { KeycloakProvider, useKeycloak } from './Keycloak';
import { initReactI18next } from "react-i18next";
import { getManagerSearchParams, isMobileApp } from './Utilities';
import Header from './header/Header';
import Content from './Content';
import i18n from "i18next";
import en from '../locales/en.json';
import de from '../locales/de.json';
import nl from '../locales/nl.json';
import LanguageDetector from 'i18next-browser-languagedetector'
import Status from './Status';
import MessageListener from './MessageListener';
import WhatsNewDialog from './WhatsNewDialog';
import dayjs from 'dayjs';

const { DEBUG, LAYOUT: { DEFAULT_LANG }, ROUTES: { HOME, LIVE, TIMELAPSE, ARCHIVE, MYSITE, ACCESS } } = window.conf

if (!i18n.isInitialized) {
    i18n.use(LanguageDetector) // detect user language
        .use(initReactI18next) // pass the i18n instance to react-i18next
        .init({
            debug: DEBUG,
            fallbackLng: 'de', // use de if detected language is not available
            interpolation: { escapeValue: false }, // not needed for react as it escapes by default
            resources: {
                en: { translation: en },
                de: { translation: de },
                nl: { translation: nl }
            },
            lng: window.localStorage.getItem('btv_lang') || DEFAULT_LANG // if no value is stored for language preference in browser set flag to default 
        })
    
    // sync dayjs based on i18n language
    dayjs.locale(i18n.language)
    
    // update dayjs locale when i18n language changes
    i18n.on('languageChanged', (lng) => dayjs.locale(lng))
}

const theme = createTheme({
    palette: {
        primary: { main: "#003366" },
        secondary: { main: "#FFFFFF" }
    }
})

const ProtectedRoute = ({ component }) => {

    const { hasRole, isLoggedIn, isUsersPortal } = useKeycloak()
    const { isManager, isSingleProject } = getManagerSearchParams()

    const isAuthenticated = isLoggedIn() // without isUsersPortal() because user logged in with wrong credentials has chance to logout and try again
    const isProtected = component.protected
    const isProtectedComplete = HOME?.protected // protected home suggests that all other routes are protected too
    const isUsers = isUsersPortal()
    const hasAccess = hasRole(component.role)
    const hasHeader = !isProtectedComplete || isUsers
    const hasBackInitial = (!isManager || isSingleProject) && isMobileApp

    DEBUG && console.log(`Route: ${component.path} isAuthenticated: ${isAuthenticated} isProtected: ${isProtected} isProtectedComplete: ${isProtectedComplete} isUsers: ${isUsers} hasAccess: ${hasAccess} `)

    if (isProtected && !hasAccess) {

        if (isAuthenticated) {

            if (hasHeader) {
                return (
                    <Fragment>
                        <Header />
                        <Status code={403} controls={["header", "logout", "back"]} />
                    </Fragment>
                )
            }

            if (hasBackInitial) {
                return <Status code={403} controls={["logout", "back"]} />
            }

            return <Status code={403} controls={["logout"]} />

        } else {

            if (hasHeader) {
                return (
                    <Fragment>
                        <Header />
                        <Status code={403} controls={["header", "login", "back"]} />
                    </Fragment>
                )
            }

            if (hasBackInitial) {
                return <Status code={403} controls={["login", "back"]} />
            }

            return <Status code={403} controls={["login"]} />

        }
    }

    return (
        <Fragment>
            <Header />
            <Content component={component.id} />
            <WhatsNewDialog />
        </Fragment>
    )
}

export default function Wrapper() {

    return (
        <StrictMode>
            <ReduxProvider store={store}>
                <MessageListener />
                <KeycloakProvider>
                    <SubscriptionProvider>
                        <ThemeProvider theme={theme}>
                            <RouterProvider>
                                <Routes>
                                    <Route path={HOME?.path} element={<ProtectedRoute component={HOME} />} />
                                    <Route path={LIVE?.path} element={<ProtectedRoute component={LIVE} />} />
                                    <Route path={TIMELAPSE?.path} element={<ProtectedRoute component={TIMELAPSE} />} />
                                    <Route path={ARCHIVE?.path} element={<ProtectedRoute component={ARCHIVE} />} />
                                    <Route path={MYSITE?.path} element={<ProtectedRoute component={MYSITE} />} />
                                    <Route path={ACCESS?.path} element={<ProtectedRoute component={ACCESS} />} />
                                    <Route path="*" element={<Status code={404} controls={["back"]} />} />
                                </Routes>
                            </RouterProvider>
                        </ThemeProvider>
                    </SubscriptionProvider>
                </KeycloakProvider>
            </ReduxProvider>
        </StrictMode>
    )
} 